import { useDialogManager } from "@Context/common/general/dialog-manager-context";
import React, { ComponentType, Suspense, useEffect, useRef, useState } from "react";

/**
 * Custom error boundary component to catch rendering errors in dialogs
 */
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Dialog error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

/**
 * DialogWrapper component to handle suspense and errors for dialog rendering
 */
const DialogWrapper = ({ Component, props, onClose }: { Component: ComponentType<any>; props: any; onClose: () => void }) => {
  return (
    <Suspense fallback={<div style={{ display: "none" }} />}>
      <ErrorBoundary>
        <Component open={true} onClose={onClose} {...props} />
      </ErrorBoundary>
    </Suspense>
  );
};

/**
 * DialogRegistryService - Centralized registration point for all application dialogs
 *
 * This component registers all dialogs with the DialogManager when the application starts
 * and also handles rendering active dialogs.
 */
const DialogRegistryService: React.FC = () => {
  const { registerDialog, closeDialog, isDialogOpen } = useDialogManager();
  const hasRegistered = useRef(false);
  const [activeDialogs, setActiveDialogs] = useState<
    Record<
      string,
      {
        Component: ComponentType<any>;
        props: Record<string, any>;
        instanceId: string;
      }
    >
  >({});

  // Subscribe to the dialog state in the context
  useEffect(() => {
    // Function to handle dialog state updates from DialogManagerContext
    const handleDialogStateUpdate = (dialogStates: any) => {
      const updatedActiveDialogs: Record<string, any> = {};

      // Process dialog states and collect active dialogs
      Object.entries(dialogStates).forEach(([type, state]: [string, any]) => {
        if (state.open && !state.loading && state.Component) {
          updatedActiveDialogs[type] = {
            Component: state.Component,
            props: state.props || {},
            instanceId: state.instanceId,
          };
        }
      });

      setActiveDialogs(updatedActiveDialogs);
    };

    // Register for dialog state updates
    const unsubscribe = (window as any).__DIALOG_MANAGER_SUBSCRIBE_TO_STATE__(handleDialogStateUpdate);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Only register once to prevent infinite loops
    if (hasRegistered.current) return;

    // Register ProfileDialog
    registerDialog("profile", {
      component: () => import("@Components/web-portal/account/profile-dialog/portal-profile-dialog"),
      props: {
        // Default props if needed
      },
    });

    // Register DemoWelcomeDialog
    registerDialog("demo-welcome", {
      component: () => import("@Components/web-portal/demo/portal-demo-welcome-dialog"),
      props: {},
    });

    // Register DemoCreatingAccountDialog
    registerDialog("demo-creating-account", {
      component: () => import("@Components/web-portal/demo/portal-demo-creating-account-dialog"),
      props: {},
    });

    registerDialog("training-plans-onboarding", {
      component: () => import("@Components/training-plans/training-plans-onboarding-dialog/training-plans-onboarding-dialog"),
      props: {},
    });

    registerDialog("bootcamp-welcome", {
      component: () => import("@Components/game/bootcamp/bootcamp-welcome-dialog"),
      props: {},
    });

    registerDialog("outstanding-invoice", {
      component: () => import("@Components/web-portal/payment/portal-outstanding-invoice-dialog"),
      props: {},
    });

    // Additional dialogs can be registered here
    // registerDialog('other-dialog', { component: () => import('path/to/other/dialog') });

    // Mark as registered
    hasRegistered.current = true;
  }, [registerDialog]);

  // Render active dialogs
  return (
    <>
      {/* Render all active dialogs within the app's provider context */}
      {Object.entries(activeDialogs).map(([type, { Component, props, instanceId }]) => (
        <DialogWrapper key={instanceId} Component={Component} props={props} onClose={() => closeDialog(type)} />
      ))}
    </>
  );
};

export default DialogRegistryService;
